import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Device } from '../types';
import {
  PCB_TYPE_OPTIONS,
  MODEL_TYPE_OPTIONS,
  getPcbRevisionOptions,
  getHardwareTableDefaults,
} from './flasher/hardwarePartitionOptions';

interface HardwareProfileModalProps {
  apiBaseUrl: string;
  device: Device;
  isOpen: boolean;
  onClose: () => void;
  onUpdated?: (device: Device) => void;
}

const FACTORY_TESTED_OPTIONS = [
  { value: '0', label: 'Not tested (0)' },
  { value: '1', label: 'Factory tested (1)' },
  { value: '2', label: 'Double Tested (2)' },
];

function resolvePcbTypeValue(raw: string | undefined, modelName: string): string {
  if (raw === '0' || raw === '1') return raw;
  if (raw === 'Fluora') return '0';
  if (raw === 'Monos') return '1';
  return getHardwareTableDefaults(modelName).pcbType;
}

function resolveModelTypeValue(raw: string | undefined, modelName: string): string {
  if (raw === '0' || raw === '1' || raw === '2' || raw === '3') return raw;
  if (raw === 'Fluora Floor') return '0';
  if (raw === 'Fluora Mini') return '1';
  if (raw === 'Monos') return '2';
  if (raw === 'Fluora Uno') return '3';
  return getHardwareTableDefaults(modelName).modelType;
}

const HardwareProfileModal: React.FC<HardwareProfileModalProps> = ({
  apiBaseUrl,
  device,
  isOpen,
  onClose,
  onUpdated,
}) => {
  const [pcbType, setPcbType] = useState('0');
  const [pcbRevision, setPcbRevision] = useState('');
  const [modelType, setModelType] = useState('0');
  const [factoryTested, setFactoryTested] = useState('2');
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;

    const nextPcbType = resolvePcbTypeValue(device.hardware?.pcbType, device.model);
    const nextModelType = resolveModelTypeValue(device.hardware?.modelType, device.model);
    const revisionOptions = getPcbRevisionOptions(nextPcbType);
    const existingRevision = device.hardware?.pcbRevision || '';
    const nextRevision = revisionOptions.includes(existingRevision)
      ? existingRevision
      : (revisionOptions[0] || '');

    setPcbType(nextPcbType);
    setModelType(nextModelType);
    setPcbRevision(nextRevision);
    setFactoryTested('2');
    setConfirming(false);
    setError(null);
  }, [isOpen, device]);

  useEffect(() => {
    const options = getPcbRevisionOptions(pcbType);
    if (pcbRevision && !options.includes(pcbRevision)) {
      setPcbRevision(options[0] || '');
    }
  }, [pcbType, pcbRevision]);

  if (!isOpen) return null;

  const commandPreview =
    `set hardware_profile pcb_revision ${pcbRevision || '?'} pcb_type ${pcbType} factory_tested ${factoryTested} model ${modelType}`;

  const canSubmit = pcbRevision !== '' && !submitting;

  const handlePcbTypeChange = (value: string) => {
    setPcbType(value);
    if (value === '1') {
      setModelType('2');
    } else if (modelType === '2') {
      setModelType('1');
    }
  };

  const handleModelTypeChange = (value: string) => {
    setModelType(value);
    setPcbType(value === '2' ? '1' : '0');
  };

  const handleUpdate = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);

    try {
      const response = await axios.post(
        `${apiBaseUrl}/devices/${device.serial}/hardware-profile`,
        {
          pcbRevision,
          pcbType,
          factoryTested,
          model: modelType,
        },
        { withCredentials: true }
      );

      if (response.data.device) {
        onUpdated?.(response.data.device);
      }
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to send hardware profile command');
      setConfirming(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Update Hardware Profile</h5>
            <button
              type="button"
              className="btn-close"
              aria-label="Close"
              onClick={onClose}
              disabled={submitting}
            />
          </div>

          <div className="modal-body">
            <p className="text-muted small mb-3">
              Device <span className="font-monospace">{device.serial}</span>
              {device.firmware ? <> · firmware {device.firmware}</> : null}
            </p>

            {!confirming ? (
              <div className="row g-3">
                <div className="col-12">
                  <label className="form-label fw-bold" htmlFor="hw-profile-pcb-type">PCB Type</label>
                  <select
                    id="hw-profile-pcb-type"
                    className="form-select"
                    value={pcbType}
                    onChange={(e) => handlePcbTypeChange(e.target.value)}
                  >
                    {PCB_TYPE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
                <div className="col-12">
                  <label className="form-label fw-bold" htmlFor="hw-profile-pcb-revision">PCB Revision</label>
                  <select
                    id="hw-profile-pcb-revision"
                    className={`form-select${pcbRevision === '' ? ' text-muted' : ''}`}
                    value={pcbRevision}
                    onChange={(e) => setPcbRevision(e.target.value)}
                  >
                    <option value="" disabled>Choose PCB Revision...</option>
                    {getPcbRevisionOptions(pcbType).map((rev) => (
                      <option key={rev} value={rev}>Rev {rev}</option>
                    ))}
                  </select>
                </div>
                <div className="col-12">
                  <label className="form-label fw-bold" htmlFor="hw-profile-model-type">Model Type</label>
                  <select
                    id="hw-profile-model-type"
                    className="form-select"
                    value={modelType}
                    onChange={(e) => handleModelTypeChange(e.target.value)}
                  >
                    {MODEL_TYPE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
                <div className="col-12">
                  <label className="form-label fw-bold" htmlFor="hw-profile-factory-tested">Factory Tested</label>
                  <select
                    id="hw-profile-factory-tested"
                    className="form-select"
                    value={factoryTested}
                    onChange={(e) => setFactoryTested(e.target.value)}
                  >
                    {FACTORY_TESTED_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
                <div className="col-12">
                  <div className="bg-light border rounded p-2 small font-monospace text-break">
                    {commandPreview}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-2">
                <p
                  className="fw-bold mb-3"
                  style={{ color: '#dc3545', fontSize: '1.15rem', lineHeight: 1.35 }}
                >
                  WARNING: This will overwrite the hardware profile on device {device.serial} over MQTT.
                  An incorrect profile can cause the device to malfunction.
                </p>
                <p className="small text-muted mb-0 font-monospace text-break">{commandPreview}</p>
              </div>
            )}

            {error && (
              <div className="alert alert-danger py-2 mt-3 mb-0">{error}</div>
            )}
          </div>

          <div className="modal-footer">
            {!confirming ? (
              <>
                <button type="button" className="btn btn-secondary" onClick={onClose} disabled={submitting}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={!canSubmit}
                  onClick={() => setConfirming(true)}
                >
                  Update on Device
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setConfirming(false)}
                  disabled={submitting}
                >
                  Back
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  disabled={!canSubmit}
                  onClick={handleUpdate}
                >
                  {submitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                      Sending...
                    </>
                  ) : (
                    'Confirm Update on Device'
                  )}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HardwareProfileModal;
